import React, { useState } from 'react'
import { 
  Building2, Users, CheckCircle, AlertCircle, 
  ChevronDown, ChevronRight, Plus, Search,
  Package, Activity, Trash2, CreditCard, RefreshCw,
  Calendar, DollarSign, X, ExternalLink, Clock
} from 'lucide-react'

export default function OrganizationsTab({ 
  organizations = [], 
  partners = [],
  loading,
  onAddSeats,
  onDeleteOrg,
  onViewSubscription,
  stats = {}
}) {
  const [expandedPartner, setExpandedPartner] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)

  // Group organizations by partner - only include ACTIVE partners
  const orgsByPartner = partners
    .filter(partner => partner.status === 'active')
    .reduce((acc, partner) => {
      acc[partner.partner_id] = {
        partner,
        orgs: organizations.filter(org => 
          org.partner_id === partner.partner_id && org.status === 'active'
        )
      }
      return acc
    }, {})

  // Filter by search
  const filteredPartners = Object.entries(orgsByPartner).filter(([_, data]) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      data.partner.name.toLowerCase().includes(term) ||
      data.orgs.some(org => org.name.toLowerCase().includes(term))
    )
  })

  // Calculate subscription stats (only from active orgs)
  const activeOrgs = organizations.filter(org => org.status === 'active')
  const activeSubscriptions = activeOrgs.filter(org => org.subscription_status === 'active').length
  const totalMRR = activeOrgs.reduce((sum, org) => {
    if (org.subscription_status === 'active') {
      const seats = (org.available_seats || 0) + (org.used_seats || 0)
      return sum + (seats * 14.99)
    }
    return sum
  }, 0)

  const handleOpenSubscription = (org) => {
    setSelectedOrg(org)
    setSubscriptionModalOpen(true)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats - Only count active */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <StatCard 
          icon={Building2}
          iconBg="bg-green-500/100/20"
          iconColor="text-green-500"
          value={activeOrgs.length}
          label="Active Organizations"
        />
        <StatCard 
          icon={Users}
          iconBg="bg-green-500/20"
          iconColor="text-green-600"
          value={partners.filter(p => p.status === 'active').length}
          label="Active Partners"
        />
        <StatCard 
          icon={RefreshCw}
          iconBg="bg-purple-500/20"
          iconColor="text-purple-400"
          value={activeSubscriptions}
          label="Subscriptions"
        />
        <StatCard 
          icon={Package}
          iconBg="bg-blue-500/20"
          iconColor="text-blue-400"
          value={activeOrgs.reduce((sum, o) => sum + (o.used_seats || 0), 0)}
          label="Seats Used"
        />
        <StatCard 
          icon={DollarSign}
          iconBg="bg-emerald-500/100/20"
          iconColor="text-emerald-400"
          value={`$${totalMRR.toFixed(0)}`}
          label="Est. MRR"
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <input
          type="text"
          placeholder="Search partners or organizations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      </div>

      {/* Partners with Organizations */}
      <div className="space-y-4">
        {filteredPartners.length === 0 ? (
          <div className="bg-zinc-800 rounded-xl p-12 border border-zinc-700 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-zinc-500" />
            <p className="font-medium text-white">No organizations found</p>
            <p className="text-sm text-zinc-400 mt-1">
              {searchTerm ? 'Try a different search term' : 'Organizations will appear here when partners register them'}
            </p>
          </div>
        ) : (
          filteredPartners.map(([partnerId, data]) => (
            <PartnerCard
              key={partnerId}
              partner={data.partner}
              organizations={data.orgs}
              isExpanded={expandedPartner === partnerId}
              onToggle={() => setExpandedPartner(expandedPartner === partnerId ? null : partnerId)}
              onAddSeats={onAddSeats}
              onDeleteOrg={onDeleteOrg}
              onViewSubscription={handleOpenSubscription}
              searchTerm={searchTerm}
            />
          ))
        )}
      </div>

      {/* Subscription Modal */}
      {subscriptionModalOpen && selectedOrg && (
        <SubscriptionModal 
          org={selectedOrg}
          onClose={() => {
            setSubscriptionModalOpen(false)
            setSelectedOrg(null)
          }}
          onAddSeats={onAddSeats}
        />
      )}
    </div>
  )
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label }) {
  return (
    <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-zinc-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

function PartnerCard({ partner, organizations, isExpanded, onToggle, onAddSeats, onDeleteOrg, onViewSubscription, searchTerm }) {
  const totalSeats = organizations.reduce((sum, org) => sum + (org.available_seats || 0) + (org.used_seats || 0), 0)
  const usedSeats = organizations.reduce((sum, org) => sum + (org.used_seats || 0), 0)
  const activeSubscriptions = organizations.filter(org => org.subscription_status === 'active').length

  // Highlight matching orgs
  const matchingOrgs = searchTerm 
    ? organizations.filter(org => org.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : organizations

  return (
    <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
      {/* Partner Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-900 transition-colors"
      >
        <div className="flex items-center gap-4">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-zinc-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-zinc-500" />
          )}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-white">{partner.name}</p>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                partner.status === 'active' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {partner.status}
              </span>
            </div>
            <p className="text-sm text-zinc-400">{partner.contact_email}</p>
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <div className="text-right">
            <p className="font-medium text-white">{organizations.length}</p>
            <p className="text-zinc-400">Organizations</p>
          </div>
          <div className="text-right">
            <p className="font-medium text-white">{activeSubscriptions}</p>
            <p className="text-zinc-400">Subscriptions</p>
          </div>
          <div className="text-right">
            <p className="font-medium text-white">{usedSeats} / {totalSeats}</p>
            <p className="text-zinc-400">Seats Used</p>
          </div>
        </div>
      </button>

      {/* Organizations List */}
      {isExpanded && (
        <div className="border-t border-zinc-700">
          {organizations.length === 0 ? (
            <div className="px-6 py-8 text-center text-zinc-400">
              <Users className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
              <p>No organizations registered yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {organizations.map((org) => (
                <OrgRow 
                  key={org.organization_id} 
                  org={org} 
                  onAddSeats={onAddSeats}
                  onDeleteOrg={onDeleteOrg}
                  onViewSubscription={onViewSubscription}
                  isHighlighted={matchingOrgs.includes(org) && searchTerm}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function OrgRow({ org, onAddSeats, onDeleteOrg, onViewSubscription, isHighlighted }) {
  const hasSubscription = org.subscription_status === 'active' || org.stripe_subscription_id
  const subscriptionStatus = org.subscription_status || 'none'
  
  const statusColors = {
    active: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-zinc-800 text-zinc-300',
    past_due: 'bg-amber-500/20 text-amber-400',
    none: 'bg-zinc-800 text-zinc-400'
  }

  return (
    <div className={`px-6 py-4 flex items-center justify-between ${isHighlighted ? 'bg-yellow-500/10' : 'hover:bg-zinc-900'}`}>
      <div className="flex items-center gap-4 pl-9">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
          {org.name?.charAt(0) || '?'}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-white">{org.name}</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              org.status === 'active' 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-zinc-800 text-zinc-300'
            }`}>
              {org.status}
            </span>
            {/* Subscription Badge */}
            {hasSubscription && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[subscriptionStatus]}`}>
                <RefreshCw className="w-3 h-3" />
                {subscriptionStatus === 'active' ? 'Subscribed' : subscriptionStatus}
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-400">
            {org.billing_email} • ID: {org.organization_id?.slice(0, 20)}...
          </p>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="text-right">
          <p className="text-sm font-medium text-white">{org.team_count || 0} teams</p>
          <p className="text-xs text-zinc-400">synced</p>
        </div>
        <div className="text-right min-w-[100px]">
          <div className="flex items-center gap-2 justify-end">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              (org.available_seats || 0) > 0 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {org.available_seats || 0} avail
            </span>
            <span className="text-xs text-zinc-400">
              / {org.used_seats || 0} used
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onViewSubscription(org)}
            className="p-2 text-purple-400 hover:bg-purple-500/10 rounded-lg"
            title="View Subscription"
            data-testid={`view-subscription-${org.organization_id}`}
          >
            <CreditCard className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAddSeats(org.organization_id, org.name)}
            className="p-2 text-green-500 hover:bg-indigo-50 rounded-lg"
            title="Add Seats"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDeleteOrg(org.organization_id, org.name)}
            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
            title="Delete Organization"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}


function SubscriptionModal({ org, onClose, onAddSeats }) {
  const hasSubscription = org.subscription_status === 'active' || org.stripe_subscription_id
  const totalSeats = (org.available_seats || 0) + (org.used_seats || 0)
  const monthlyEstimate = totalSeats * 14.99

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-zinc-800 rounded-2xl w-full max-w-lg mx-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="subscription-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Subscription Details</h2>
            <p className="text-sm text-zinc-400">{org.name}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg"
            data-testid="close-subscription-modal"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Subscription Status Card */}
          <div className={`p-4 rounded-xl ${
            hasSubscription 
              ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200' 
              : 'bg-zinc-900 border border-zinc-700'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${hasSubscription ? 'bg-purple-500/20' : 'bg-zinc-700'}`}>
                  <RefreshCw className={`w-5 h-5 ${hasSubscription ? 'text-purple-400' : 'text-zinc-400'}`} />
                </div>
                <div>
                  <p className="font-medium text-white">
                    {hasSubscription ? 'Active Subscription' : 'No Subscription'}
                  </p>
                  <p className="text-sm text-zinc-400">
                    {hasSubscription ? 'Monthly billing active' : 'One-time purchases only'}
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                hasSubscription 
                  ? org.subscription_status === 'active' 
                    ? 'bg-green-500/20 text-green-400'
                    : org.subscription_status === 'past_due'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-zinc-800 text-zinc-300'
                  : 'bg-zinc-800 text-zinc-300'
              }`}>
                {org.subscription_status || 'None'}
              </span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-zinc-400 mb-1">
                <Package className="w-4 h-4" />
                <span className="text-sm">Total Seats</span>
              </div>
              <p className="text-2xl font-bold text-white">{totalSeats}</p>
              <p className="text-xs text-zinc-400">{org.used_seats || 0} used, {org.available_seats || 0} available</p>
            </div>
            <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-zinc-400 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">Monthly Cost</span>
              </div>
              <p className="text-2xl font-bold text-white">${monthlyEstimate.toFixed(2)}</p>
              <p className="text-xs text-zinc-400">@ $14.99/seat</p>
            </div>
          </div>

          {/* Details List */}
          <div className="space-y-3">
            <DetailRow 
              icon={Building2}
              label="Organization ID"
              value={org.organization_id}
            />
            <DetailRow 
              icon={Calendar}
              label="Created"
              value={formatDate(org.created_at)}
            />
            {org.subscription_activated_at && (
              <DetailRow 
                icon={CheckCircle}
                label="Subscription Started"
                value={formatDate(org.subscription_activated_at)}
              />
            )}
            {org.webhook_url && (
              <DetailRow 
                icon={ExternalLink}
                label="Webhook URL"
                value={org.webhook_url}
                truncate
              />
            )}
          </div>

          {/* Webhook Status */}
          {org.webhook_url && (
            <div className="bg-blue-500/10 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">Webhook Configured</span>
              </div>
              <p className="text-xs text-blue-400 mt-1">
                Events: {org.webhook_events?.join(', ') || 'None configured'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-700 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-zinc-300 hover:bg-zinc-800 rounded-lg"
          >
            Close
          </button>
          <button
            onClick={() => {
              onAddSeats(org.organization_id, org.name)
              onClose()
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            data-testid="add-seats-from-modal"
          >
            <Plus className="w-4 h-4" />
            Add Seats
          </button>
        </div>
      </div>
    </div>
  )
}

function DetailRow({ icon: Icon, label, value, truncate }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-700 last:border-0">
      <div className="flex items-center gap-2 text-zinc-400">
        <Icon className="w-4 h-4" />
        <span className="text-sm">{label}</span>
      </div>
      <span className={`text-sm font-medium text-white ${truncate ? 'max-w-[200px] truncate' : ''}`}>
        {value}
      </span>
    </div>
  )
}

